import { Link } from "react-router-dom";

/* PACKAGES */
import { motion } from "framer-motion";
import Select from "react-select";

/* UTILS */
import { containerAnimation, animation } from "../../utils/animation";

/* COMPONENTS */
import { FormInput } from "../../components/core/input";
import { FormButton } from "../../components/core/buttons";

/* STATE OPTIONS */
const stateOptions = [
  { value: "United States", label: "United States" },
  { value: "Canada", label: "Canada" },
  { value: "China", label: "China" },
  { value: "United Kingdom", label: "United Kingdom" },
  { value: "Belgium", label: "Belgium" },
  { value: "Mexico", label: "Mexico" },
  { value: "Germany", label: "Germany" },
  { value: "Japan", label: "Japan" },
];

const RegisterPage = () => {
  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-white py-6 xl:py-14 lg:py-14"
    >
      <motion.div variants={animation} className="p-0">
        <div className="container">
          {/* HEADING */}
          <div className="text-center">
            <h2 className="text-4xl font-fraunces-bold tracking-tight text-black">
              Apply for registration
            </h2>
            <p className="mt-1 text-md font-source font-normal text-headerGrey">
              You are entering our wholesale portal. Fill out the form below to
              set up your business account.
            </p>
          </div>

          {/* FORM */}
          <form className="w-[95%] mx-auto mt-10">
            <div className="grid grid-cols-3 gap-5">
              {/* LEFT SECTION */}
              <div className="col-span-2 h-fit sticky top-0">
                {/* CONTACT INFORMATION */}
                <div className="bg-greyBg border border-greyBorder shadow rounded-lg p-8">
                  <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                    Contact Information
                  </h2>
                  <div className="mt-4">
                    <div className="grid grid-cols-2 gap-x-5 gap-y-4">
                      {/* FIRST NAME */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          First name <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="firstName"
                            placeholder="Enter your first name"
                          />
                        </div>
                      </div>

                      {/* LAST NAME */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Last name <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="lastName"
                            placeholder="Enter your last name"
                          />
                        </div>
                      </div>

                      {/* EMAIL ADDRESS */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Email address <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="email"
                            name="email"
                            placeholder="Enter your email address"
                          />
                        </div>
                      </div>

                      {/* PHONE NUMBER */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Phone <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="phone"
                            name="phone"
                            placeholder="Enter your phone"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BUSINESS INFORMATION */}
                <div className="mt-5 bg-greyBg border border-greyBorder shadow rounded-lg p-8">
                  <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                    Business Information
                  </h2>
                  <div className="mt-4">
                    <div className="grid grid-cols-2 gap-x-5 gap-y-4">
                      {/* LEGAL BUSINESS NAME */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Legal Business name{" "}
                          <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="legalBusinessName"
                            placeholder="Enter your legal business name"
                          />
                        </div>
                      </div>

                      {/* DBA NAME */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          DBA name <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="dbaName"
                            placeholder="Enter your DBA Name"
                          />
                        </div>
                      </div>

                      {/* ADDRESS LINE ONE */}
                      <div className="col-span-2">
                        <label className="text-sm font-source font-medium text-black">
                          Address Line 1 <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="addressLine1"
                            placeholder="Enter your address line 1"
                          />
                        </div>
                      </div>

                      {/* COUNTRY */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Country <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <Select
                            options={stateOptions}
                            className="react-select"
                            classNamePrefix="state"
                            placeholder="Select your country"
                          />
                        </div>
                      </div>

                      {/* STATE */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          State <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="state"
                            placeholder="Enter your state"
                          />
                        </div>
                      </div>

                      {/* CITY */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          City <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="city"
                            placeholder="Enter your city"
                          />
                        </div>
                      </div>

                      {/* ZIPCODE */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Zipcode <span className="text-red-400">*</span>
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="number"
                            name="zipcode"
                            placeholder="Enter your zipcode"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT SECTION */}
              <div className="col-span-1">
                {/* STATE TAX ID */}
                <div className="bg-greyBg border border-greyBorder shadow rounded-lg p-8">
                  <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                    State Tax
                  </h2>
                  <div className="mt-4">
                    <div className="grid grid-cols-1 gap-y-4">
                      {/* STATE TAX ID */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          State Tax ID
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="stateTaxId"
                            placeholder="Enter your state tax ID"
                          />
                        </div>
                      </div>

                      {/* RESALE CERTIFICATE */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Resale (Sales Tax) Certificate
                        </label>
                        <div className="mt-1">
                          <div className="w-full px-3 py-2.5 bg-white border border-greyBorder rounded-md">
                            <input
                              type="file"
                              name="resaleCertificate"
                              id="resaleCertificate"
                              className="w-full block font-source font-semibold text-sm text-black p-0 focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FEDERAL EIN DOCUMENT NUMBER */}
                <div className="mt-5 bg-greyBg border border-greyBorder shadow rounded-lg p-8">
                  <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                    Federal EIN
                  </h2>
                  <div className="mt-4">
                    <div className="grid grid-cols-1 gap-y-4">
                      {/* DOCUMENT NUMBER */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Federal EIN Document Number
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="einNumber"
                            placeholder="Enter your document number"
                          />
                        </div>
                      </div>

                      {/* RESALE CERTIFICATE */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Federal EIN Document (FEIN)
                        </label>
                        <div className="mt-1">
                          <div className="w-full px-4 py-3 bg-white border border-greyBorder rounded-md">
                            <input
                              type="file"
                              name="einDocument"
                              id="einDocument"
                              className="w-full block font-source font-semibold text-sm text-black p-0 focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* TOBACCO LICENSE NUMBER */}
                <div className="mt-5 bg-greyBg border border-greyBorder shadow rounded-lg p-8">
                  <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                    Tobacco License Number
                  </h2>
                  <div className="mt-4">
                    <div className="grid grid-cols-1 gap-y-4">
                      {/* TOBACCO LICENSE NUMBER */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Tobacco License Number
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="licenseNumber"
                            placeholder="Enter your tobacco license number"
                          />
                        </div>
                      </div>

                      {/* RESALE CERTIFICATE */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Tobacco License
                        </label>
                        <div className="mt-1">
                          <div className="w-full px-4 py-3 bg-white border border-greyBorder rounded-md">
                            <input
                              type="file"
                              name="tobaccoLicense"
                              id="tobaccoLicense"
                              className="w-full block font-source font-semibold text-sm text-black p-0 focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* DRIVING LICENSE NUMBER */}
                <div className="mt-5 bg-greyBg border border-greyBorder shadow rounded-lg p-8">
                  <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                    Driving License Number
                  </h2>
                  <div className="mt-4">
                    <div className="grid grid-cols-1 gap-y-4">
                      {/* DRIVING LICENSE NUMBER */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Driving License Number
                        </label>
                        <div className="mt-1">
                          <FormInput
                            type="text"
                            name="drivingLicenseNumber"
                            placeholder="Enter your driving license number"
                          />
                        </div>
                      </div>

                      {/* DRIVING LICENSE */}
                      <div className="col-span-1">
                        <label className="text-sm font-source font-medium text-black">
                          Driving License
                        </label>
                        <div className="mt-1">
                          <div className="w-full px-4 py-3 bg-white border border-greyBorder rounded-md">
                            <input
                              type="file"
                              name="drivingLicense"
                              id="drivingLicense"
                              className="w-full block font-source font-semibold text-sm text-black p-0 focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ACTION BUTTON */}
            <div className="flex items-center justify-between">
              <p className="mt-3 text-base font-source font-normal text-center">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="text-base font-source font-semibold text-primaryRed"
                >
                  Sign in here
                </Link>
              </p>
              <FormButton
                className="mt-8 w-fit text-base rounded-full"
                label="Create account"
                type="submit"
              />
            </div>
          </form>
        </div>
      </motion.div>
    </motion.section>
  );
};

export default RegisterPage;
